import { Box, Typography, List, ListItem } from '@mui/material'
import { FeatureFlagIdCopilotInsights } from 'c1gen/features'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { CopilotIcon } from 'components/core/icons/copilot'
import { UsageLabel, StatusLabel, ReviewRiskLabel, ReviewUserPropTitleLabel, EmploymentStatusLabel } from 'components/core/insights'
import { AccessConflictLabel } from 'components/core/insights/insight-kinds/analysis/AccessConflict'
import { InsightRecommendationStyles } from 'components/core/insights/styles'

import { useFeatureChecker } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { TaskValue } from 'lib/actions/task'
import { AccessConflictOutcome, UserPropOutcome } from 'pbts/c1/api/analysis/v1/analysis'
import { InsightRecommendation, InsightType } from 'pbts/c1/api/task/v1/insights'
import { Any } from 'pbts/google/protobuf/any'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'

export type RecommendationInsightsProps = {
    task: TaskValue
}

const RecommendationInsightsDenyContent: FC<RecommendationInsightsProps> = ({ task }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    const recommendDeny = hasCopilotInsights && task.recommendation === InsightRecommendation.DENY
    if (!recommendDeny || !insights) {
        return null
    }

    let recommendationTitle = (
        <FormattedMessage
            id="copilot_insights_recommendation_title"
            defaultMessage="Copilot recommends removing this access"
        />
    )
    if (task.type?.taskType.oneofKind === 'grant') {
        recommendationTitle = (
            <FormattedMessage
                id="copilot_insights_recommendation_title"
                defaultMessage="Copilot recommends denying this access"
            />
        )
    }

    return (
        <Box sx={InsightRecommendationStyles.recommendations}>
            <Typography variant="h6" component="div">
                {recommendationTitle}
            </Typography>
            <List sx={InsightRecommendationStyles.list}>
                {insights.map((insight, i) => {
                    if (insight.kind.oneofKind === 'usage' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_usage_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <UsageLabel usage={insight.kind.usage} app={task.app} appUser={task.appUser} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'userStatus' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_user_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <StatusLabel userStatus={insight.kind.userStatus.userStatus} user={task.identityUser} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'employmentStatus' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_employment_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <EmploymentStatusLabel
                                    employmentStatus={insight.kind.employmentStatus.employmentStatus}
                                    employmentStatusSources={insight.kind.employmentStatus.employmentStatusSources}
                                    user={task.identityUser}
                                />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'analysis'
                        && insight.type === InsightType.ALERT
                        && Any.typeUrlToName(insight.kind.analysis.outcome?.outcome?.typeUrl || '') === AccessConflictOutcome.typeName
                    ) {
                        return (
                            <ListItem key={`rec_item_employment_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <AccessConflictLabel task={task} />
                            </ListItem>
                        )
                    }
                    return null
                })}
            </List>
        </Box>
    )
}

const RecommendationInsightsReviewContent: FC<RecommendationInsightsProps> = ({ task }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    const recommendDeny = hasCopilotInsights && task.recommendation === InsightRecommendation.REVIEW
    if (!recommendDeny || !insights) {
        return null
    }

    return (
        <Box sx={InsightRecommendationStyles.recommendations}>
            <Typography variant="h6">
                <FormattedMessage
                    id="copilot_insights_review_title"
                    defaultMessage="Copilot suggests taking a closer look at this access"
                />
            </Typography>
            <List sx={InsightRecommendationStyles.list}>
                {insights.map((insight, i) => {
                    if (insight.kind.oneofKind === 'risk' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_usage_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <ReviewRiskLabel risk={insight.kind.risk} task={task} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'analysis' &&
                        insight.type === InsightType.NEGATIVE &&
                        Any.typeUrlToName(insight.kind.analysis.outcome?.outcome?.typeUrl || '') === UserPropOutcome.typeName
                    ) {
                        const userPropOutcome = insight.kind.analysis.outcome?.outcome as unknown as UserPropOutcome
                        const isTitleUserProps = userPropOutcome.input?.userProp?.key === 'title'
                        if (isTitleUserProps) {
                            return (
                                <ListItem key={`rec_item_user_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                    <ReviewUserPropTitleLabel outcome={insight.kind.analysis.outcome} insightType={insight.type} />
                                </ListItem>
                            )
                        }
                        return null
                    }
                    return null
                })}
            </List>
        </Box>
    )
}

export const RecommendationInsights: FC<RecommendationInsightsProps> = ({ task }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    const recommendDeny = hasCopilotInsights && task.recommendation === InsightRecommendation.DENY
    const recommendReview = hasCopilotInsights && task.recommendation === InsightRecommendation.REVIEW
    if (!insights || (!recommendDeny && !recommendReview)) {
        return null
    }
    return (
        <Box sx={sxc(
            InsightRecommendationStyles.container,
            [!!recommendDeny, InsightRecommendationStyles.denyContainer],
            [!!recommendReview, InsightRecommendationStyles.reviewContainer],
        )}
        >
            <CopilotIcon sx={sxc(
                InsightRecommendationStyles.icon,
                [!!recommendDeny, InsightRecommendationStyles.denyIcon],
                [!!recommendReview, InsightRecommendationStyles.reviewIcon],
            )}
            />
            {recommendDeny ? <RecommendationInsightsDenyContent task={task} /> : <RecommendationInsightsReviewContent task={task} />}
        </Box>
    )
}

export type RecommendationInsightsTooltipProps = {
    task: TaskValue
    children: React.ReactElement
}

export const RecommendationDenyTooltip: FC<RecommendationInsightsTooltipProps> = ({ task, children }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    const recommendDeny = hasCopilotInsights && task.recommendation === InsightRecommendation.DENY
    if (!recommendDeny || !insights) {
        return <>{children}</>
    }

    return (
        <C1Tooltip placement="top" title={<RecommendationInsightsDenyContent task={task} />}>
            {children}
        </C1Tooltip>
    )
}

export const RecommendationReviewTooltip: FC<RecommendationInsightsTooltipProps> = ({ task, children }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    const recommendReview = hasCopilotInsights && task.recommendation === InsightRecommendation.REVIEW
    if (!recommendReview || !insights) {
        return <>{children}</>
    }

    return (
        <C1Tooltip title={<RecommendationInsightsReviewContent task={task} />}>
            {children}
        </C1Tooltip>
    )
}
